<template>
  <b-modal ref="modal"
           size="xl"
           modal-class="annotation-viewer-modal"
           body-class="annotation-viewer__body"
           hide-footer
           @shown="onShown"
           @hidden="onHidden"
           data-qid="annotation-viewer">
    <template #modal-header>
      <div class="annotation-viewer__header">
        <div class="annotation-viewer__title">
          <h5 class="mb-0 text-truncate">{{ file.file_name }}</h5>
          <div class="text-muted small">{{ contextLabel }}</div>
        </div>
        <span class="btn btn-secondary cursor-pointer" @click="hide" data-qid="annotation-viewer-close">
          <font-awesome-icon icon="times"/>
        </span>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5 text-muted">
      <font-awesome-icon icon="spinner" spin/>
    </div>

    <div v-else-if="error" class="alert alert-danger m-3" data-qid="annotation-viewer-error">{{ error }}</div>

    <template v-else-if="info">
      <!-- Office files are annotated on a PDF copy; the original stays unchanged -->
      <div v-if="isOffice && !renditionReady" class="annotation-viewer__office" data-qid="annotation-viewer-office">
        <div v-if="renditionStatus === 'pending'" class="text-center text-muted">
          <font-awesome-icon icon="spinner" spin class="mr-2"/>{{ $t('annotations.office.converting') }}
        </div>
        <template v-else>
          <h5>{{ $t('annotations.office.title') }}</h5>
          <p>{{ $t('annotations.office.explanation', { file: file.file_name }) }}</p>
          <p v-if="renditionStatus === 'failed'" class="text-danger">{{ $t('annotations.office.failed') }}</p>
          <p v-if="!info.can_edit" class="text-muted">{{ $t('annotations.office.not_converted') }}</p>
          <div class="text-right">
            <b-button variant="secondary" @click="hide">{{ $t('system.cancel') }}</b-button>
            <b-button variant="outline-secondary" class="ml-2" @click="downloadOriginal">{{ $t('annotations.office.download_original') }}</b-button>
            <b-button v-if="info.can_edit"
                      variant="success"
                      class="ml-2"
                      :disabled="busy"
                      @click="convert"
                      data-qid="annotation-viewer-convert">
              {{ renditionStatus === 'failed' ? $t('annotations.office.retry') : $t('annotations.office.convert') }}
            </b-button>
          </div>
        </template>
      </div>

      <div v-else class="annotation-viewer__workspace">
        <div v-if="isOffice" class="annotation-viewer__banner" data-qid="annotation-viewer-office-banner">
          <font-awesome-icon icon="info-circle" class="mr-2"/>
          {{ $t('annotations.office.banner', { file: file.file_name }) }}
          <a href="#" class="ml-1" @click.prevent="downloadOriginal">{{ $t('annotations.office.download_original') }}</a>
        </div>
        <div v-if="!info.can_edit" class="annotation-viewer__banner annotation-viewer__banner--muted" data-qid="annotation-viewer-read-only">
          <font-awesome-icon icon="lock" class="mr-2"/>{{ readOnlyLabel }}
        </div>

        <div class="annotation-viewer__toolbar">
          <div class="btn-group" v-if="info.can_edit">
            <button v-for="item in tools"
                    :key="item.key"
                    type="button"
                    class="btn btn-sm"
                    :class="tool === item.key ? 'btn-primary' : 'btn-outline-secondary'"
                    v-b-tooltip.hover
                    :title="$t('annotations.tools.' + item.key)"
                    @click="setTool(item.key)"
                    :data-qid="'annotation-tool-' + item.key">
              <font-awesome-icon :icon="item.icon"/>
            </button>
          </div>
          <div class="annotation-viewer__colours" v-if="info.can_edit">
            <span v-for="item in colours"
                  :key="item"
                  class="annotation-viewer__colour"
                  :class="{'annotation-viewer__colour--active': colour === item}"
                  :style="{ backgroundColor: item }"
                  v-b-tooltip.hover
                  :title="$t('annotations.colour')"
                  @click="setColour(item)"></span>
          </div>
          <div class="annotation-viewer__zoom">
            <span class="btn btn-sm btn-outline-secondary" @click="zoom(-1)" v-b-tooltip.hover :title="$t('annotations.zoom_out')">
              <font-awesome-icon icon="magnifying-glass-minus"/>
            </span>
            <span class="text-muted mx-2">{{ Math.round(scale * 100) }}%</span>
            <span class="btn btn-sm btn-outline-secondary" @click="zoom(1)" v-b-tooltip.hover :title="$t('annotations.zoom_in')">
              <font-awesome-icon icon="magnifying-glass-plus"/>
            </span>
          </div>
        </div>

        <div class="annotation-viewer__main">
          <div class="annotation-viewer__pages" ref="pages" @scroll="renderVisiblePages">
            <div v-if="!pages.length" class="text-center p-5 text-muted">
              <font-awesome-icon icon="spinner" spin/>
            </div>
            <div v-for="page in pages"
                 :key="'page-' + page.number"
                 class="annotation-viewer__page"
                 :data-page="page.number"
                 :style="{ width: page.width * scale + 'px', height: page.height * scale + 'px' }">
              <img v-if="kind === 'image'" :src="imageUrl" draggable="false" alt=""/>
              <canvas v-else :ref="'canvas-' + page.number"></canvas>

              <svg class="annotation-viewer__layer"
                   :class="'annotation-viewer__layer--' + tool"
                   :width="page.width * scale"
                   :height="page.height * scale"
                   @mousedown="startDrawing(page, $event)">
                <g v-for="item in annotationsOnPage(page.number)"
                   :key="'annotation-' + item.annotation.id"
                   class="annotation-viewer__shape"
                   :class="{'annotation-viewer__shape--selected': selectedId === item.annotation.id}"
                   @mousedown.stop="startMoving(item.annotation, page, $event)">
                  <annotation-shape :annotation="item.annotation" :width="page.width * scale" :height="page.height * scale"></annotation-shape>
                </g>
                <g v-if="draft && draft.page === page.number" class="annotation-viewer__shape annotation-viewer__shape--draft">
                  <annotation-shape :annotation="draft" :width="page.width * scale" :height="page.height * scale"></annotation-shape>
                </g>
              </svg>

              <span v-for="item in annotationsOnPage(page.number)"
                    :key="'badge-' + item.annotation.id"
                    class="annotation-viewer__badge"
                    :class="{'annotation-viewer__badge--selected': selectedId === item.annotation.id}"
                    :style="badgeStyle(item.annotation, page)"
                    @mousedown.stop="startMoving(item.annotation, page, $event)">{{ item.number }}</span>
            </div>
          </div>

          <div class="annotation-viewer__sidebar">
            <!-- A drawn shape gets its comment here before it's saved -->
            <div v-if="draft && draft.complete" class="annotation-viewer__draft" data-qid="annotation-draft">
              <div class="text-bold mb-2">{{ $t('annotations.new') }} · {{ $t('annotations.tools.' + draft.type) }}</div>
              <b-form-textarea ref="draftComment"
                               v-model="draft.comment"
                               rows="3"
                               max-rows="8"
                               :placeholder="$t('annotations.comment_placeholder')"
                               data-qid="annotation-draft-comment"></b-form-textarea>
              <div class="text-right mt-2">
                <b-button size="sm" variant="secondary" @click="draft = null">{{ $t('system.cancel') }}</b-button>
                <b-button size="sm" variant="success" class="ml-2" :disabled="busy" @click="saveDraft" data-qid="annotation-draft-save">{{ $t('system.save') }}</b-button>
              </div>
            </div>

            <b-form-select v-if="!info.task_id && info.tasks.length"
                           v-model="taskFilter"
                           :options="filterOptions"
                           size="sm"
                           class="mb-2"
                           data-qid="annotation-filter"></b-form-select>

            <div v-if="!listed.length" class="text-muted small" data-qid="annotation-empty">
              {{ $t('annotations.empty') }}
            </div>

            <div v-for="item in listed"
                 :key="'item-' + item.annotation.id"
                 class="annotation-viewer__item"
                 :class="{'annotation-viewer__item--selected': selectedId === item.annotation.id}"
                 @click="select(item.annotation, true)"
                 data-qid="annotation-item">
              <div class="annotation-viewer__item-header">
                <span class="annotation-viewer__item-number" :style="{ backgroundColor: item.annotation.color || defaultColour }">{{ item.number }}</span>
                <span class="text-bold">{{ $t('annotations.tools.' + item.annotation.type) }}</span>
                <span class="text-muted ml-1" v-if="pages.length > 1">· {{ $t('annotations.page', { page: item.annotation.page }) }}</span>
                <span class="annotation-viewer__item-task" :class="{'annotation-viewer__item-task--general': !item.annotation.project_task_id}">
                  {{ item.annotation.task_title || $t('annotations.general') }}
                </span>
              </div>
              <div class="text-muted small">
                {{ item.annotation.created_by ? item.annotation.created_by.name : '' }} · {{ item.annotation.created_at | formatDateTime }}
              </div>

              <template v-if="editingId === item.annotation.id">
                <b-form-textarea v-model="editComment" rows="3" max-rows="8" class="mt-2" @click.native.stop></b-form-textarea>
                <div class="text-right mt-2">
                  <b-button size="sm" variant="secondary" @click.stop="editingId = null">{{ $t('system.cancel') }}</b-button>
                  <b-button size="sm" variant="success" class="ml-2" :disabled="busy" @click.stop="saveComment(item.annotation)">{{ $t('system.save') }}</b-button>
                </div>
              </template>
              <div v-else-if="item.annotation.comment" class="annotation-viewer__item-comment">{{ item.annotation.comment }}</div>

              <div v-if="item.annotation.can_edit && editingId !== item.annotation.id" class="annotation-viewer__item-actions">
                <template v-if="generalId === item.annotation.id">
                  <span class="text-muted mr-2">{{ $t('annotations.make_general_confirm') }}</span>
                  <a href="#" class="mr-2" @click.prevent.stop="generalId = null">{{ $t('system.no') }}</a>
                  <a href="#" @click.prevent.stop="makeGeneral(item.annotation)" data-qid="annotation-make-general-confirm">{{ $t('system.yes') }}</a>
                </template>
                <template v-else-if="deletingId === item.annotation.id">
                  <span class="text-muted mr-2">{{ $t('annotations.delete_confirm') }}</span>
                  <a href="#" class="mr-2" @click.prevent.stop="deletingId = null">{{ $t('system.no') }}</a>
                  <a href="#" class="text-danger" @click.prevent.stop="remove(item.annotation)" data-qid="annotation-delete-confirm">{{ $t('system.yes') }}</a>
                </template>
                <template v-else>
                  <a href="#" class="mr-3" @click.prevent.stop="startEditing(item.annotation)">{{ $t('system.edit') }}</a>
                  <a v-if="item.annotation.can_make_general"
                     href="#"
                     class="mr-3"
                     @click.prevent.stop="generalId = item.annotation.id; deletingId = null"
                     data-qid="annotation-make-general">{{ $t('annotations.make_general') }}</a>
                  <a href="#" class="text-danger" @click.prevent.stop="deletingId = item.annotation.id; generalId = null" data-qid="annotation-delete">{{ $t('system.delete') }}</a>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import { downloadDocument } from '@/util/openDocument.js';

const COLOURS = ['#e03131', '#f59f00', '#2f9e44', '#1971c2', '#7048e8'];
const HIGHLIGHT_OPACITY = 0.35;
const MIN_SCALE = 0.25;
const MAX_SCALE = 4;

// One annotation drawn in the page's SVG layer; geometry is in fractions (0-1) of the displayed page
const AnnotationShape = {
  name: 'AnnotationShape',
  functional: true,
  props: {
    annotation: { type: Object, required: true },
    width: { type: Number, required: true },
    height: { type: Number, required: true }
  },
  render(h, context) {
    const { annotation, width, height } = context.props;
    const g = annotation.geometry || {};
    const colour = annotation.color || COLOURS[0];
    const stroke = { stroke: colour, 'stroke-width': 2, fill: 'none', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' };
    // Wide invisible stroke so thin shapes are easy to click
    const hit = { stroke: 'transparent', 'stroke-width': 12, fill: 'none', class: 'annotation-viewer__hit' };

    switch (annotation.type) {
      case 'box':
      case 'highlight': {
        const rect = { x: g.x * width, y: g.y * height, width: Math.max(1, g.w * width), height: Math.max(1, g.h * height) };
        return annotation.type === 'box'
          ? [h('rect', { attrs: Object.assign({}, rect, hit, { fill: 'transparent' }) }), h('rect', { attrs: Object.assign({}, rect, stroke) })]
          : [h('rect', { attrs: Object.assign({}, rect, { fill: colour, 'fill-opacity': HIGHLIGHT_OPACITY }) })];
      }
      case 'arrow': {
        const x1 = g.x1 * width, y1 = g.y1 * height, x2 = g.x2 * width, y2 = g.y2 * height;
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const head = 12;
        const left = [x2 - head * Math.cos(angle - Math.PI / 7), y2 - head * Math.sin(angle - Math.PI / 7)];
        const right = [x2 - head * Math.cos(angle + Math.PI / 7), y2 - head * Math.sin(angle + Math.PI / 7)];
        const line = { x1: x1, y1: y1, x2: x2, y2: y2 };
        return [
          h('line', { attrs: Object.assign({}, line, hit) }),
          h('line', { attrs: Object.assign({}, line, stroke) }),
          h('polyline', { attrs: Object.assign({ points: left.join(',') + ' ' + x2 + ',' + y2 + ' ' + right.join(',') }, stroke) })
        ];
      }
      case 'freehand': {
        const points = (g.points || []).map(point => (point[0] * width) + ',' + (point[1] * height)).join(' ');
        return [h('polyline', { attrs: Object.assign({ points: points }, hit) }), h('polyline', { attrs: Object.assign({ points: points }, stroke) })];
      }
      default:
        // Notes are shown by their numbered badge only
        return [];
    }
  }
};

export default {
  name: 'DocumentAnnotationViewer',
  components: {
    AnnotationShape
  },
  data() {
    return {
      project: null,
      file: {},
      task: null,
      info: null,
      loading: false,
      error: '',
      busy: false,
      pages: [],
      scale: 1,
      imageUrl: null,
      tool: 'select',
      colour: COLOURS[0],
      colours: COLOURS,
      defaultColour: COLOURS[0],
      draft: null,
      selectedId: null,
      editingId: null,
      editComment: '',
      deletingId: null,
      generalId: null,
      taskFilter: null,
      tools: [
        { key: 'select', icon: 'arrow-pointer' },
        { key: 'box', icon: 'draw-square' },
        { key: 'highlight', icon: 'highlighter' },
        { key: 'arrow', icon: 'arrow-right' },
        { key: 'note', icon: 'note-sticky' },
        { key: 'freehand', icon: 'signature' }
      ]
    };
  },
  created() {
    // pdf.js objects and DOM helpers stay out of Vue's reactivity
    this.pdfDoc = null;
    this.renderedScale = {};
    this.renderFrame = null;
    this.pollTimer = null;
    this.pointer = null;
  },
  beforeDestroy() {
    this.reset();
  },
  computed: {
    baseUrl() {
      return 'projects/' + this.project.slug + '/documents/' + this.file.id + '/annotations';
    },
    params() {
      return this.task ? { task_id: this.task.id } : {};
    },
    kind() {
      return this.info ? this.info.document.kind : null;
    },
    isOffice() {
      return this.kind === 'office';
    },
    renditionStatus() {
      return this.info && this.info.rendition ? this.info.rendition.status : null;
    },
    renditionReady() {
      return this.renditionStatus === 'ready';
    },
    contextLabel() {
      return this.task
        ? this.$t('annotations.context_task', { task: this.task.title || '' })
        : this.$t('annotations.context_general');
    },
    readOnlyLabel() {
      if (this.info && this.info.locked) {
        return this.$t(this.task ? 'annotations.locked_task' : 'annotations.locked_project');
      }
      return this.$t('annotations.read_only');
    },
    // Numbered in creation order, so numbers stay the same when the list is filtered
    numbered() {
      return _.map(this.info ? this.info.annotations : [], (annotation, index) => ({ annotation: annotation, number: index + 1 }));
    },
    listed() {
      if (this.taskFilter === null) {
        return this.numbered;
      }
      return _.filter(this.numbered, item => (item.annotation.project_task_id || 0) === this.taskFilter);
    },
    filterOptions() {
      return [
        { value: null, text: this.$t('annotations.filter_all') },
        { value: 0, text: this.$t('annotations.general') }
      ].concat(_.map(this.info.tasks, task => ({ value: task.id, text: task.title })));
    }
  },
  methods: {
    /**
     * @param {Object} options { project: {slug}, file: {id, file_name}, task: {id, title} | null }
     */
    show(options) {
      this.reset();
      this.project = options.project;
      this.file = options.file;
      this.task = options.task || null;
      this.$refs.modal.show();
      this.load();
    },
    hide() {
      this.$refs.modal.hide();
    },
    onShown() {
      if (this.pages.length) {
        this.fitToWidth();
      }
    },
    // Lists refresh their annotation counts, so viewing the file shows what was just added or deleted
    onHidden() {
      this.reset();
      this.$emit('closed');
    },
    reset() {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
      if (this.renderFrame) {
        window.cancelAnimationFrame(this.renderFrame);
        this.renderFrame = null;
      }
      if (this.pdfDoc) {
        this.pdfDoc.destroy();
        this.pdfDoc = null;
      }
      if (this.imageUrl) {
        window.URL.revokeObjectURL(this.imageUrl);
      }
      this.stopPointer();
      this.renderedScale = {};
      Object.assign(this, { info: null, pages: [], imageUrl: null, error: '', draft: null, selectedId: null, editingId: null, deletingId: null, generalId: null, taskFilter: null, tool: 'select', busy: false });
    },
    load() {
      let self = this;
      this.loading = true;

      piincHttp.get(this.baseUrl, { params: this.params }).then(function(response) {
        self.loading = false;
        self.info = response.data;
        if (!self.info.can_edit) {
          self.tool = 'select';
        }
        if (!self.isOffice || self.renditionReady) {
          self.loadSource();
        } else if (self.renditionStatus === 'pending') {
          self.poll();
        }
      }, function(error) {
        self.loading = false;
        self.error = error && error.data && error.data.message ? error.data.message : self.$t('annotations.load_failed');
      });
    },
    // Office conversion runs in the background; check every few seconds until the copy is ready
    poll() {
      let self = this;
      clearTimeout(this.pollTimer);
      this.pollTimer = setTimeout(function() {
        piincHttp.get(self.baseUrl, { params: self.params }).then(function(response) {
          if (!self.info) {
            return;
          }
          self.info = response.data;
          if (self.renditionReady) {
            self.loadSource();
          } else if (self.renditionStatus === 'pending') {
            self.poll();
          }
        }, function() {
          self.poll();
        });
      }, 3000);
    },
    convert() {
      let self = this;
      this.busy = true;
      piincHttp.post(this.baseUrl + '/rendition', this.params).then(function(response) {
        self.busy = false;
        self.info.rendition = response.data.rendition;
        if (self.renditionReady) {
          self.loadSource();
        } else {
          self.poll();
        }
      }, function(error) {
        self.busy = false;
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    },
    downloadOriginal() {
      downloadDocument(this.file);
    },
    loadSource() {
      let self = this;
      piincHttp.get(this.baseUrl + '/source', { params: this.params, responseType: 'arraybuffer' }).then(function(response) {
        if (!self.info) {
          return;
        }
        if (self.kind === 'image') {
          self.loadImage(response.data, response.headers['content-type']);
        } else {
          self.loadPdf(response.data);
        }
      }, function() {
        self.error = self.$t('annotations.load_failed');
      });
    },
    loadImage(data, type) {
      let self = this;
      this.imageUrl = window.URL.createObjectURL(new Blob([data], { type: type }));
      const image = new Image();
      image.onload = function() {
        self.pages = [{ number: 1, width: image.naturalWidth, height: image.naturalHeight }];
        self.$nextTick(self.fitToWidth);
      };
      image.onerror = function() {
        self.error = self.$t('annotations.load_failed');
      };
      image.src = this.imageUrl;
    },
    async loadPdf(data) {
      try {
        const pdfjs = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf');
        const worker = await import(/* webpackChunkName: "pdfjs" */ '!!file-loader?name=js/[name].[hash:8].[ext]!pdfjs-dist/legacy/build/pdf.worker.min.js');
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default || worker;

        const pdfDoc = await pdfjs.getDocument({ data: new Uint8Array(data) }).promise;
        if (!this.info) {
          pdfDoc.destroy();
          return;
        }
        this.pdfDoc = pdfDoc;

        let pages = [];
        for (let number = 1; number <= pdfDoc.numPages; number++) {
          const page = await pdfDoc.getPage(number);
          // The viewport includes the page rotation, so the layer matches the page as it's displayed
          const viewport = page.getViewport({ scale: 1 });
          pages.push({ number: number, width: viewport.width, height: viewport.height });
        }
        this.pages = pages;
        this.$nextTick(this.fitToWidth);
      } catch (e) {
        this.error = this.$t('annotations.load_failed');
      }
    },
    fitToWidth() {
      const container = this.$refs.pages;
      if (!container || !this.pages.length) {
        return;
      }
      const widest = _.max(_.map(this.pages, 'width'));
      this.setScale((container.clientWidth - 40) / widest);
    },
    zoom(direction) {
      this.setScale(this.scale * (direction > 0 ? 1.25 : 0.8));
    },
    setScale(scale) {
      this.scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale || 1));
      this.$nextTick(this.renderVisiblePages);
    },
    // pdf.js pages are only drawn when they are on screen (or close to it), once per scroll frame
    renderVisiblePages() {
      if (this.kind === 'image' || !this.pdfDoc || this.renderFrame) {
        return;
      }
      this.renderFrame = window.requestAnimationFrame(() => {
        this.renderFrame = null;
        const container = this.$refs.pages;
        if (!container) {
          return;
        }
        const view = container.getBoundingClientRect();
        container.querySelectorAll('.annotation-viewer__page').forEach(element => {
          const rect = element.getBoundingClientRect();
          if (rect.bottom >= view.top - 300 && rect.top <= view.bottom + 300) {
            this.renderPage(parseInt(element.getAttribute('data-page'), 10));
          }
        });
      });
    },
    async renderPage(number) {
      if (!this.pdfDoc || this.renderedScale[number] === this.scale) {
        return;
      }
      const scale = this.scale;
      this.renderedScale[number] = scale;

      try {
        const page = await this.pdfDoc.getPage(number);
        const canvas = this.$refs['canvas-' + number] && this.$refs['canvas-' + number][0];
        if (!canvas || scale !== this.scale) {
          throw new Error('stale');
        }
        const ratio = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * ratio });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = (viewport.width / ratio) + 'px';
        canvas.style.height = (viewport.height / ratio) + 'px';
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport }).promise;
      } catch (e) {
        // Drawn again the next time it's on screen
        if (this.renderedScale[number] === scale) {
          delete this.renderedScale[number];
        }
      }
    },
    annotationsOnPage(number) {
      return _.filter(this.listed, item => item.annotation.page === number);
    },
    // Badge at the start of the shape: top left of boxes, the tail of arrows, the first point of freehand
    anchorOf(annotation) {
      const g = annotation.geometry || {};
      switch (annotation.type) {
        case 'arrow':
          return [g.x1, g.y1];
        case 'freehand':
          return g.points && g.points.length ? g.points[0] : [0, 0];
        default:
          return [g.x, g.y];
      }
    },
    badgeStyle(annotation, page) {
      const anchor = this.anchorOf(annotation);
      return {
        left: (anchor[0] * page.width * this.scale) + 'px',
        top: (anchor[1] * page.height * this.scale) + 'px',
        backgroundColor: annotation.color || this.defaultColour
      };
    },
    setTool(tool) {
      this.tool = tool;
      this.draft = null;
    },
    setColour(colour) {
      this.colour = colour;
      const selected = this.selectedAnnotation();
      if (selected && selected.can_edit && selected.color !== colour) {
        this.update(selected, { color: colour });
      }
    },
    selectedAnnotation() {
      return this.info ? _.find(this.info.annotations, { id: this.selectedId }) : null;
    },
    select(annotation, scrollTo) {
      this.selectedId = annotation.id;
      if (scrollTo && this.$refs.pages) {
        const element = this.$refs.pages.querySelector('[data-page="' + annotation.page + '"]');
        if (element) {
          const anchor = this.anchorOf(annotation);
          this.$refs.pages.scrollTop = element.offsetTop + anchor[1] * element.offsetHeight - 80;
        }
      }
    },
    pointOf(event, element) {
      const rect = element.getBoundingClientRect();
      return [
        Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
        Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))
      ];
    },
    // Drawing: the shape follows the mouse, and its comment is asked for when the mouse is released
    startDrawing(page, event) {
      if (event.button !== 0) {
        return;
      }
      if (this.tool === 'select' || !this.info.can_edit) {
        this.selectedId = null;
        return;
      }
      event.preventDefault();
      const element = event.currentTarget;
      const start = this.pointOf(event, element);
      const geometry = {
        box: { x: start[0], y: start[1], w: 0, h: 0 },
        highlight: { x: start[0], y: start[1], w: 0, h: 0 },
        arrow: { x1: start[0], y1: start[1], x2: start[0], y2: start[1] },
        note: { x: start[0], y: start[1] },
        freehand: { points: [start] }
      }[this.tool];

      this.selectedId = null;
      this.draft = { id: 'draft', page: page.number, type: this.tool, geometry: geometry, color: this.colour, comment: '', complete: false };

      if (this.tool === 'note') {
        this.completeDraft();
        return;
      }

      this.startPointer(moveEvent => {
        const point = this.pointOf(moveEvent, element);
        const g = this.draft.geometry;
        if (this.draft.type === 'arrow') {
          this.draft.geometry = Object.assign({}, g, { x2: point[0], y2: point[1] });
        } else if (this.draft.type === 'freehand') {
          const last = _.last(g.points);
          if (Math.hypot(point[0] - last[0], point[1] - last[1]) > 0.002) {
            this.draft.geometry = { points: g.points.concat([point]) };
          }
        } else {
          this.draft.geometry = {
            x: Math.min(start[0], point[0]),
            y: Math.min(start[1], point[1]),
            w: Math.abs(point[0] - start[0]),
            h: Math.abs(point[1] - start[1])
          };
        }
      }, () => {
        const g = this.draft.geometry;
        const tooSmall = {
          box: g.w < 0.005 || g.h < 0.005,
          highlight: g.w < 0.005 || g.h < 0.005,
          arrow: Math.hypot(g.x2 - g.x1, g.y2 - g.y1) < 0.01,
          freehand: (g.points || []).length < 2
        }[this.draft.type];
        if (tooSmall) {
          this.draft = null;
        } else {
          this.completeDraft();
        }
      });
    },
    completeDraft() {
      this.draft.complete = true;
      this.$nextTick(() => {
        if (this.$refs.draftComment) {
          this.$refs.draftComment.focus();
        }
      });
    },
    saveDraft() {
      let self = this;
      const draft = this.draft;
      this.busy = true;

      piincHttp.post(this.baseUrl, Object.assign({}, this.params, {
        type: draft.type,
        page: draft.page,
        geometry: draft.geometry,
        comment: draft.comment,
        color: draft.color
      })).then(function(response) {
        self.busy = false;
        self.draft = null;
        self.info.annotations.push(response.data.annotation);
        self.selectedId = response.data.annotation.id;
        self.$toastr('success', self.$t('annotations.saved'));
      }, function(error) {
        self.busy = false;
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    },
    // Moving: drag a selected shape; the new position is saved when the mouse is released
    startMoving(annotation, page, event) {
      if (event.button !== 0 || this.tool !== 'select') {
        return;
      }
      event.preventDefault();
      this.select(annotation);
      if (!annotation.can_edit) {
        return;
      }

      const element = event.currentTarget.closest('.annotation-viewer__page');
      const start = this.pointOf(event, element);
      const original = JSON.parse(JSON.stringify(annotation.geometry));
      let moved = false;

      this.startPointer(moveEvent => {
        const point = this.pointOf(moveEvent, element);
        const dx = point[0] - start[0];
        const dy = point[1] - start[1];
        moved = moved || Math.abs(dx) > 0.002 || Math.abs(dy) > 0.002;
        annotation.geometry = this.translate(original, dx, dy);
      }, () => {
        if (moved) {
          this.update(annotation, { geometry: annotation.geometry }, original);
        }
      });
    },
    translate(geometry, dx, dy) {
      const clamp = value => Math.min(1, Math.max(0, value));
      if (geometry.points) {
        return { points: geometry.points.map(point => [clamp(point[0] + dx), clamp(point[1] + dy)]) };
      }
      let result = Object.assign({}, geometry);
      ['x', 'x1', 'x2'].forEach(key => {
        if (key in result) {
          result[key] = clamp(result[key] + dx);
        }
      });
      ['y', 'y1', 'y2'].forEach(key => {
        if (key in result) {
          result[key] = clamp(result[key] + dy);
        }
      });
      return result;
    },
    startPointer(onMove, onUp) {
      this.stopPointer();
      const move = event => onMove(event);
      const up = event => {
        this.stopPointer();
        onUp(event);
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      this.pointer = { move: move, up: up };
    },
    stopPointer() {
      if (this.pointer) {
        window.removeEventListener('mousemove', this.pointer.move);
        window.removeEventListener('mouseup', this.pointer.up);
        this.pointer = null;
      }
    },
    startEditing(annotation) {
      this.editingId = annotation.id;
      this.editComment = annotation.comment || '';
    },
    saveComment(annotation) {
      this.update(annotation, { comment: this.editComment }).then(saved => {
        if (saved) {
          this.editingId = null;
        }
      });
    },
    // Resolves true when saved; on errors the shape goes back to where it was
    update(annotation, changes, previousGeometry) {
      let self = this;
      this.busy = true;

      return piincHttp.put(this.baseUrl + '/' + annotation.id, Object.assign({}, this.params, changes)).then(function(response) {
        self.busy = false;
        const index = _.findIndex(self.info.annotations, { id: annotation.id });
        if (index >= 0) {
          self.$set(self.info.annotations, index, response.data.annotation);
        }
        return true;
      }, function(error) {
        self.busy = false;
        if (previousGeometry) {
          annotation.geometry = previousGeometry;
        }
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
        return false;
      });
    },
    // A task-specific annotation becomes general: it then shows on every task the file is on
    makeGeneral(annotation) {
      let self = this;
      this.busy = true;

      piincHttp.post(this.baseUrl + '/' + annotation.id + '/general', this.params).then(function(response) {
        self.busy = false;
        self.generalId = null;
        const index = _.findIndex(self.info.annotations, { id: annotation.id });
        if (index >= 0) {
          self.$set(self.info.annotations, index, response.data.annotation);
        }
        self.$toastr('success', self.$t('annotations.made_general'));
      }, function(error) {
        self.busy = false;
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    },
    remove(annotation) {
      let self = this;
      this.busy = true;

      piincHttp.delete(this.baseUrl + '/' + annotation.id, { params: this.params }).then(function() {
        self.busy = false;
        self.deletingId = null;
        self.info.annotations = _.reject(self.info.annotations, { id: annotation.id });
        self.$toastr('success', self.$t('annotations.deleted'));
      }, function(error) {
        self.busy = false;
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    }
  }
};
</script>
